"use client";
import type { LinkComponentType } from "../../../index";
import { ContactMetaDisplay } from "../../ContactMetaDisplay";
import type { CellComponentProps, CellEmphasis } from "../DataTable.types";
import { getCellContainerClasses, resolveValue } from "./cellUtils";

export interface ContactMetaCellProps<T = unknown> extends CellComponentProps<T> {
  /** First name (can be a function to extract from row) */
  firstName: string | ((row: T) => string);

  /** Last name (can be a function to extract from row) */
  lastName: string | ((row: T) => string);

  /** Email address (optional, can be a function to extract from row) */
  email?: string | ((row: T) => string | undefined);

  /** Avatar image URL (optional, can be a function to extract from row) */
  avatarUrl?: string | ((row: T) => string | undefined);

  /** Layout variant - stacked (vertical) or inline (horizontal) */
  layout?: "stacked" | "inline";

  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Link to contact detail page (optional) */
  href?: string | ((row: T) => string);

  /** Custom link component (e.g., Next.js Link) */
  LinkComponent?: LinkComponentType;

  /** Link variant for the name */
  linkVariant?: "primary" | "secondary" | "muted" | "unstyled" | "default" | "brand";

  /** Text emphasis level - "strong"/"high" for bold, "normal" for regular, "low" for muted */
  emphasis?: CellEmphasis;

  /** Show email address (only for stacked layout) */
  showEmail?: boolean;

  /** Badge element to display next to the name (can be a function to extract from row) */
  badge?: React.ReactNode | ((row: T) => React.ReactNode);

  /** Alignment */
  align?: "left" | "center" | "right";

  /** Additional classes */
  className?: string;

  /**
   * Entity identifier for PII masking (can be a function to extract from row)
   * Example: (row) => `contact:${row.id}`
   */
  piiEntity?: string | ((row: T) => string | undefined);
}

/**
 * ContactMetaCell
 *
 * DataTable cell component for displaying contact information with avatar.
 * Wraps ContactMetaDisplay for use in DataTable columns with layout="stacked" by default.
 *
 * @example
 * ```tsx
 * // In DataTable column definition
 * {
 *   id: "contact",
 *   label: "Contact",
 *   cell: ContactMetaCell,
 *   cellProps: {
 *     firstName: (row) => row.firstName,
 *     lastName: (row) => row.lastName,
 *     email: (row) => row.email,
 *     avatarUrl: (row) => row.avatarUrl,
 *     href: (row) => `/contacts/${row.id}`,
 *     showEmail: true,
 *     layout: "stacked",
 *     size: "sm"
 *   }
 * }
 * ```
 */
export function ContactMetaCell<T = unknown>({
  row,
  context,
  firstName,
  lastName,
  email,
  avatarUrl,
  layout = "stacked",
  size = "sm",
  href,
  LinkComponent,
  linkVariant = "secondary",
  showEmail = false,
  badge,
  emphasis = "normal",
  align = "left",
  className,
  piiEntity,
}: ContactMetaCellProps<T>) {
  const { isLoading } = context;

  // Resolve dynamic values
  const firstNameValue = resolveValue(firstName, row);
  const lastNameValue = resolveValue(lastName, row);
  const emailValue = resolveValue(email, row);
  const avatarUrlValue = resolveValue(avatarUrl, row);
  const hrefValue = resolveValue(href, row);
  const badgeValue = resolveValue(badge, row);
  const piiEntityValue = resolveValue(piiEntity, row);

  const containerClasses = getCellContainerClasses(align, className);

  return (
    <div className={containerClasses}>
      <ContactMetaDisplay
        firstName={firstNameValue}
        lastName={lastNameValue}
        email={emailValue}
        avatarUrl={avatarUrlValue}
        layout={layout}
        size={size}
        href={hrefValue}
        LinkComponent={LinkComponent}
        linkVariant={linkVariant}
        showEmail={showEmail}
        badge={badgeValue}
        emphasis={emphasis}
        isLoading={isLoading}
        piiEntity={piiEntityValue}
      />
    </div>
  );
}
